import { useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock, CheckCircle, MessageSquare, Headphones, Zap } from 'lucide-react';
import { contactAPI } from '../utils/api';
import { useSettings } from '../context/SettingsContext';
import toast from 'react-hot-toast';

const CATEGORIES = [
  { value: 'product', label: { en: 'Product Inquiry', fr: 'Question Produit', ar: 'استفسار منتج' }, icon: '📦' },
  { value: 'order', label: { en: 'Order Support', fr: 'Support Commande', ar: 'دعم الطلب' }, icon: '🛍️' },
  { value: 'return', label: { en: 'Return / Refund', fr: 'Retour / Remboursement', ar: 'إرجاع / استرداد' }, icon: '🔄' },
  { value: 'sales', label: { en: 'Sales / Bulk', fr: 'Ventes / Gros', ar: 'مبيعات / جملة' }, icon: '💼' },
  { value: 'payment', label: { en: 'Payment Issue', fr: 'Problème de Paiement', ar: 'مشكلة الدفع' }, icon: '💳' },
  { value: 'other', label: { en: 'Other', fr: 'Autre', ar: 'أخرى' }, icon: '💬' },
];

const INFO_ITEMS = [
  { icon: Mail, label: 'Email', value: 'support@techstore.com', color: '#7c6dfa' },
  { icon: Phone, label: 'Phone', value: '+216 74 000 000', color: '#22c55e' },
  { icon: MapPin, label: 'Address', value: 'Sfax, Tunisia', color: '#f59e0b' },
  { icon: Clock, label: 'Hours', value: 'Mon – Sat, 9am – 6pm', color: '#38bdf8' },
];

const EMPTY = { name: '', email: '', subject: '', message: '', category: 'product' };

export default function ContactPage() {
  const { t, lang } = useSettings();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await contactAPI.submit(form);
      setSent(true);
      setForm(EMPTY);
      toast.success('Message sent successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send message');
    }
    setLoading(false);
  };

  return (
    <div>
      {/* Hero */}
      <section style={{ padding: '80px 0 60px', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'var(--gradient-hero)', pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--accent-dim)', border: '1px solid rgba(124,109,250,0.25)', borderRadius: 30, padding: '7px 18px', marginBottom: 20 }}>
            <MessageSquare size={14} color="var(--accent)" />
            <span style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 700 }}>Support</span>
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 6vw, 64px)', fontWeight: 700, letterSpacing: '-0.04em', marginBottom: 16 }}>
            {lang === 'ar' ? 'تواصل معنا' : lang === 'fr' ? 'Contactez-nous' : 'Get in Touch'}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 17, maxWidth: 520, margin: '0 auto' }}>
            {lang === 'ar' ? 'فريقنا مستعد للمساعدة. أرسل لنا رسالة وسنرد في أقرب وقت ممكن.' : lang === 'fr' ? "Notre équipe est prête à vous aider. Envoyez-nous un message et nous répondrons dès que possible." : "Our team is ready to help. Send us a message and we'll respond as soon as possible."}
          </p>
        </div>
      </section>

      <div className="container" style={{ padding: '72px 28px' }}>
        <div className="contact-layout" style={{ display: 'grid', gridTemplateColumns: '1fr 420px', gap: 48 }}>

          {/* ── FORM ── */}
          <div>
            {sent ? (
              <div style={{ textAlign: 'center', padding: '80px 40px', background: 'var(--bg-card)', border: '1.5px solid var(--success)', borderRadius: 24 }}>
                <div style={{ width: 72, height: 72, background: 'rgba(34,197,94,0.12)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                  <CheckCircle size={36} color="var(--success)" />
                </div>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, marginBottom: 12 }}>
                  {lang === 'ar' ? 'تم إرسال رسالتك!' : lang === 'fr' ? 'Message envoyé !' : 'Message Sent!'}
                </h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: 16, marginBottom: 32 }}>
                  {lang === 'ar' ? 'شكراً للتواصل معنا. سنرد عليك خلال 24 ساعة.' : lang === 'fr' ? "Merci de nous avoir contacté. Nous vous répondrons dans les 24 heures." : "Thanks for reaching out. We'll get back to you within 24 hours."}
                </p>
                <button className="btn btn-primary btn-lg" onClick={() => setSent(false)}>
                  {lang === 'ar' ? 'إرسال رسالة أخرى' : lang === 'fr' ? 'Envoyer un autre message' : 'Send Another Message'}
                </button>
              </div>
            ) : (
              <div className="card" style={{ padding: 36 }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
                  {lang === 'ar' ? 'أرسل رسالة' : lang === 'fr' ? 'Envoyer un message' : 'Send a Message'}
                </h2>
                <p style={{ color: 'var(--text-muted)', marginBottom: 28, fontSize: 14 }}>
                  {lang === 'ar' ? 'اختر الفئة وأخبرنا كيف يمكننا مساعدتك' : lang === 'fr' ? "Choisissez une catégorie et dites-nous comment nous pouvons vous aider" : "Choose a category and tell us how we can help"}
                </p>

                {/* Category selector */}
                <div className="form-group">
                  <label className="form-label">
                    {lang === 'ar' ? 'الفئة' : lang === 'fr' ? 'Catégorie' : 'Category'}
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 4 }}>
                    {CATEGORIES.map(cat => (
                      <button key={cat.value} type="button" onClick={() => f('category', cat.value)}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 12, border: `2px solid ${form.category === cat.value ? 'var(--accent)' : 'var(--border)'}`, background: form.category === cat.value ? 'var(--accent-dim)' : 'var(--bg-secondary)', cursor: 'pointer', color: form.category === cat.value ? 'var(--accent)' : 'var(--text-secondary)', fontSize: 13, fontWeight: form.category === cat.value ? 700 : 500, transition: 'var(--transition)', fontFamily: 'var(--font-body)' }}>
                        <span>{cat.icon}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.label[lang] || cat.label.en}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div className="form-group">
                      <label className="form-label">{lang === 'ar' ? 'الاسم' : lang === 'fr' ? 'Nom' : 'Name'} *</label>
                      <input className="form-input" value={form.name} onChange={e => f('name', e.target.value)} placeholder="John Doe" required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{lang === 'ar' ? 'البريد الإلكتروني' : 'Email'} *</label>
                      <input className="form-input" type="email" value={form.email} onChange={e => f('email', e.target.value)} placeholder="you@example.com" required />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{lang === 'ar' ? 'الموضوع' : lang === 'fr' ? 'Sujet' : 'Subject'} *</label>
                    <input className="form-input" value={form.subject} onChange={e => f('subject', e.target.value)} placeholder={lang === 'ar' ? 'موضوع رسالتك...' : lang === 'fr' ? 'Sujet de votre message...' : 'What is this about?'} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{lang === 'ar' ? 'الرسالة' : lang === 'fr' ? 'Message' : 'Message'} *</label>
                    <textarea className="form-input" rows={6} value={form.message} onChange={e => f('message', e.target.value)} placeholder={lang === 'ar' ? 'اكتب رسالتك هنا...' : lang === 'fr' ? 'Écrivez votre message ici...' : 'Describe your issue or question in detail...'} required />
                  </div>
                  <button type="submit" className="btn btn-primary btn-lg btn-full" disabled={loading}>
                    <Send size={18} />
                    {loading ? (lang === 'ar' ? 'جاري الإرسال...' : lang === 'fr' ? 'Envoi...' : 'Sending...') : (lang === 'ar' ? 'إرسال الرسالة' : lang === 'fr' ? 'Envoyer' : 'Send Message')}
                  </button>
                </form>
              </div>
            )}
          </div>

          {/* ── INFO SIDEBAR ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Contact info */}
            <div className="card">
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, marginBottom: 20 }}>
                {lang === 'ar' ? 'معلومات الاتصال' : lang === 'fr' ? 'Informations de contact' : 'Contact Info'}
              </h3>
              {INFO_ITEMS.map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 20 }}>
                  <div style={{ width: 44, height: 44, background: `${item.color}18`, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <item.icon size={20} color={item.color} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{item.label}</div>
                    <div style={{ fontSize: 15, fontWeight: 500 }}>{item.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* FAQ */}
            <div className="card">
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, marginBottom: 16 }}>
                {lang === 'ar' ? 'الأسئلة الشائعة' : lang === 'fr' ? 'FAQ' : 'Quick Answers'}
              </h3>
              {[
                { q: lang === 'ar' ? 'ما هو وقت التسليم؟' : lang === 'fr' ? 'Quel est le délai de livraison ?' : 'What is the delivery time?', a: lang === 'ar' ? '2-3 أيام عمل' : lang === 'fr' ? '2-3 jours ouvrables' : '2-3 business days' },
                { q: lang === 'ar' ? 'هل يمكنني إرجاع المنتج؟' : lang === 'fr' ? 'Puis-je retourner un produit ?' : 'Can I return a product?', a: lang === 'ar' ? 'نعم، خلال 30 يومًا' : lang === 'fr' ? 'Oui, dans les 30 jours' : 'Yes, within 30 days' },
                { q: lang === 'ar' ? 'هل المنتجات أصلية؟' : lang === 'fr' ? 'Les produits sont-ils authentiques ?' : 'Are products authentic?', a: lang === 'ar' ? '100% أصلية مع ضمان' : lang === 'fr' ? '100% authentiques avec garantie' : '100% authentic with warranty' },
              ].map((item, i) => (
                <div key={i} style={{ padding: '14px 0', borderBottom: i < 2 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{item.q}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{item.a}</div>
                </div>
              ))}
            </div>

            {/* Live chat card */}
            <div style={{ padding: 24, background: 'linear-gradient(135deg, var(--accent-dim), var(--gold-dim))', border: '1.5px solid var(--accent)', borderRadius: 20, textAlign: 'center' }}>
              <div style={{ width: 48, height: 48, background: 'var(--accent)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <Headphones size={22} color="white" />
              </div>
              <h4 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 8 }}>
                {lang === 'ar' ? 'الدعم الفوري' : lang === 'fr' ? 'Support en direct' : 'Live Support'}
              </h4>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
                {lang === 'ar' ? 'متاحون 24/7 لمساعدتك' : lang === 'fr' ? 'Disponibles 24/7 pour vous aider' : 'Available 24/7 to help you'}
              </p>
              <a href="tel:+21674000000" className="btn btn-primary btn-sm btn-full">
                {lang === 'ar' ? 'اتصل الآن' : lang === 'fr' ? 'Appeler maintenant' : 'Call Now'}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
